/**
 * Converts a data URI to Blob.
 *
 * @param {string} dataURI
 * @return {Object} Blob object.
 */
const dataURItoBlob = (dataURI) => {
  // convert base64 to raw binary data held in a string
  const byteString = atob(dataURI.split(',')[1]);

  // separate out the mime component. Example: dataURI = 'data:image/jpeg;base64,<base64_here>' -> result: image/jpeg
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

  // write the bytes of the string to an ArrayBuffer
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const typedArray = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteString.length; i++) {
    typedArray[i] = byteString.charCodeAt(i);
  }

  const dataView = new DataView(arrayBuffer);

  return new Blob([dataView], { type: mimeString });
};

/**
 * Converts a data URI to a Blob URL.
 * NOTE: Always call URL.revokeObjectURL(blobURL); once it is not longer used. Otherwise a memory leak will happen.
 *
 * @param {string} dataURI
 * @return {string} Blob URL.
 */
const dataURItoBlobURL = (dataURI) => URL.createObjectURL(dataURItoBlob(dataURI));

// Autolink bare URLs, but skip URLs already inside a markdown link (e.g. server-emitted
// `[label](https://x.com)` from unwrapped Slack links). Matching the whole `[..](..)` first
// consumes it, so its inner URL isn't re-wrapped into a broken `[x]([x](x))`.
const wrapURLsForMarkdown = (text = '') => (
  text.replace(
    /\[[^\]]*\]\([^)]*\)|(?:https?:\/\/|www(?=\.))[-.\w]+[^ \t\r\n]*/gi,
    (match) => (match.startsWith('[') ? match : `[${match}](${match})`),
  )
);

// Collapse markdown links `[label](url)` to just `label` for plain-text previews
// (channel list, etc.) that don't render markdown — otherwise the raw syntax shows.
const stripMarkdownLinks = (text = '') => (
  text.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
);

export {
  dataURItoBlob,
  dataURItoBlobURL,
  wrapURLsForMarkdown,
  stripMarkdownLinks,
};
